import { Input as AntInput } from 'antd';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../assets/theme';
import { isCuripodPastedSlide } from '../../../utils/copyAndPasteSlidesUtils';

const AntTextArea = AntInput.TextArea;

interface TextAreaProps {
  disabled?: boolean;
  fluid?: boolean;
  style?: React.CSSProperties;
  textAreaStyled?: React.CSSProperties;
  placeholder?: string;
  label?: string;
  labelStyle?: React.CSSProperties;
  error?: string;
  symbolCount?: boolean;
  maxLength?: number;
  rows?: number;
  value?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  autoSize?: any;
  autoFocus?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement> | undefined;
  onPressEnter?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  bordered?: boolean;
  className?: string;
}

export const TextAreaV2 = React.forwardRef(
  (props: TextAreaProps, ref: React.Ref<TextAreaRef>) => {
    const {
      disabled,
      fluid,
      label,
      error,
      symbolCount,
      maxLength,
      placeholder,
      value,
      onChange,
      onPressEnter,
      style,
      labelStyle,
      rows,
      autoSize,
      bordered,
      onPaste,
      autoFocus,
      textAreaStyled,
      className,
    } = props;

    const fluidValue = fluid !== undefined && fluid;
    const errorValue = error !== undefined;
    const withSymbolCount = symbolCount !== undefined && symbolCount;
    const valueLength = value === undefined || !value ? 0 : value.length;
    const withMaxLength = maxLength !== undefined && maxLength > 0;

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        // Prevent accidentally pasting curipod slide pasted payload into inputs
        const pastedText = e.clipboardData.getData('text');
        if (isCuripodPastedSlide(pastedText)) {
          e.preventDefault();
          return;
        }
        onPaste && onPaste(e);
      },
      [onPaste],
    );

    return (
      <Wrapper
        $fluid={fluidValue}
        $withSymbolCount={withSymbolCount}
        style={style ? style : {}}
      >
        <span translate="no">
          {label && <Label style={labelStyle ? labelStyle : {}}>{label}</Label>}
          <StyledTextArea
            disabled={disabled}
            $fluid={fluidValue}
            placeholder={placeholder}
            onPaste={handlePaste}
            $error={errorValue}
            value={value}
            style={textAreaStyled ? textAreaStyled : {}}
            rows={rows}
            onChange={onChange}
            onPressEnter={onPressEnter}
            autoSize={autoSize}
            bordered={bordered}
            autoFocus={autoFocus}
            className={className}
            ref={ref}
            maxLength={maxLength}
          />
          {error && <ErrorLabel>{error}</ErrorLabel>}
          {withSymbolCount && !error && (
            <CounterLabel>
              {valueLength}
              {withMaxLength && '/' + maxLength}
            </CounterLabel>
          )}
        </span>
      </Wrapper>
    );
  },
);
TextAreaV2.displayName = 'TextArea';

interface StyledTextAreaProps {
  $error: boolean;
  $fluid: boolean;
}

const StyledTextArea = styled(AntTextArea)<StyledTextAreaProps>`
  border: 1px solid #d9d9d9;
  border-radius: 5px;

  &,
  &.ant-input {
    color: ${theme.colors.black};
    font-family: 'Nunito';
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 130%;
    padding: 10px 15px;
    mix-blend-mode: normal;
    resize: none;
  }

  &:hover {
    border-color: white;
    box-shadow: none;
  }

  &:focus {
    background-color: #fbfbfc;
    border-color: #d9d9d9;
    box-shadow: none;
  }
  &::placeholder {
    color: #a5a5a5;
  }
  input {
    &:focus {
      ::placeholder,
      ::-webkit-input-placeholder {
        color: transparent;
      }
      :-ms-input-placeholder {
        color: transparent;
      }
    }
  }

  ${props =>
    props.$error &&
    css`
      border-color: ${theme.colors.red};

      &:hover,
      &:focus {
        border-color: ${theme.colors.red};
      }
    `}

  ${props =>
    props.$fluid &&
    css`
      height: 100%;
      width: 100%;
    `}
`;

interface WrapperProps {
  $fluid: boolean;
  $withSymbolCount: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  display: inline-block;

  ${props =>
    props.$fluid &&
    css`
      height: 100%;
      width: 100%;
    `}

  ${props =>
    props.$withSymbolCount &&
    css`
      position: relative;

      ${StyledTextArea} {
        margin-bottom: 0px;
      }

      ${CounterLabel} {
        position: absolute;
        bottom: -14px;
        right: 0;
      }
    `}
`;

const Label = styled.label`
  color: #a5a5a5;
  font-family: 'Nunito';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
`;

const CounterLabel = styled.label`
  color: #a5a5a5;
  font-family: 'Nunito';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
`;

const ErrorLabel = styled.label`
  color: ${theme.colors.red};
  font-family: 'Nunito';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
`;
